import { useTranslation } from "react-i18next";
import PageShell from "@/components/site/PageShell";
import PageHero from "@/components/site/PageHero";
import { openCookiePreferences } from "@/components/CookieConsent";

export default function CookiePolicy() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "it";
  const c = COPY[lang];

  return (
    <PageShell>
      <PageHero
        eyebrow={c.eyebrow}
        titleA={c.titleA}
        titleB={c.titleB}
        description={c.description}
        imageUrl="/eu-heritage-hero.png"
      />

      <section className="bg-white py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-10 space-y-10 text-[15px] text-[#1e293b] leading-[1.85]">
          {c.sections.map((s, i) => (
            <div key={i} className="space-y-3">
              <h2 className="font-display text-[26px] md:text-[30px] text-[#0f2340] leading-tight">
                {s.h}
              </h2>
              {s.p.map((para, j) => (
                <p key={j}>{para}</p>
              ))}
              {s.list && (
                <ul className="list-disc pl-6 space-y-1.5 text-[#334155]">
                  {s.list.map((li, k) => (
                    <li key={k}>{li}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          <div className="border-t border-[#e2e8f0] pt-8 mt-8">
            <button
              data-testid="reopen-cookie-preferences-btn-cookies"
              onClick={openCookiePreferences}
              className="text-[11px] tracking-[0.28em] uppercase text-[#1a365d] hover:text-[#d4af37] transition-colors font-semibold underline decoration-[#d4af37] decoration-2 underline-offset-[6px]"
            >
              {c.managePreferences}
            </button>
          </div>

          <p className="text-[12.5px] text-[#64748b] pt-6">{c.lastUpdated}</p>
        </div>
      </section>
    </PageShell>
  );
}

const COPY = {
  it: {
    eyebrow: "— Legal / Cookie Policy",
    titleA: "Cookie",
    titleB: "Policy.",
    description:
      "Questa pagina descrive quali cookie e tecnologie di tracciamento utilizza Tesori d'Europa, per quali finalità e come puoi gestire le tue preferenze.",
    managePreferences: "Gestisci le mie preferenze sui cookie",
    lastUpdated: "Ultimo aggiornamento: Febbraio 2026.",
    sections: [
      {
        h: "1. Cosa sono i cookie",
        p: [
          "I cookie sono piccoli file di testo salvati sul tuo dispositivo quando visiti un sito web. Servono a far funzionare il sito, ricordare le tue preferenze e — se autorizzati — a raccogliere informazioni statistiche o di marketing.",
        ],
      },
      {
        h: "2. Categorie di cookie utilizzati",
        p: [
          "Utilizziamo i cookie suddivisi nelle seguenti categorie. Solo quelli \"Necessari\" sono attivi di default; tutti gli altri richiedono il tuo consenso esplicito.",
        ],
      },
      {
        h: "3. Necessari",
        p: [
          "Questi cookie sono strettamente necessari al funzionamento del sito. Consentono la navigazione, l'utilizzo del form InBetween e del cambio di lingua. Non richiedono consenso in quanto senza di essi il servizio non può essere erogato.",
        ],
      },
      {
        h: "4. Funzionalità",
        p: [
          "Abilitano funzioni avanzate come la memorizzazione della lingua preferita, delle preferenze di visualizzazione e delle interazioni con i form. Vengono attivati solo con il tuo consenso.",
        ],
      },
      {
        h: "5. Esperienza",
        p: [
          "Migliorano la qualità dell'esperienza utente e permettono interazioni con contenuti multimediali esterni (es. video incorporati, mappe). Attivi solo con il tuo consenso.",
        ],
      },
      {
        h: "6. Misurazione",
        p: [
          "Ci aiutano a comprendere in forma aggregata come i visitatori usano il sito (pagine più lette, tempo di permanenza, provenienza geografica) al fine di migliorare i contenuti editoriali. Attivi solo con il tuo consenso.",
        ],
      },
      {
        h: "7. Marketing",
        p: [
          "Consentono l'erogazione di contenuti personalizzati e la misurazione delle campagne di comunicazione. Al momento, Tesori d'Europa non utilizza questa categoria di cookie ma può attivarla in futuro previo tuo consenso esplicito.",
        ],
      },
      {
        h: "8. Come gestire le preferenze",
        p: [
          "Al primo accesso al sito ti viene proposto il pannello \"Le tue scelte sulla privacy\". Puoi accettare tutti i cookie, rifiutarli o personalizzare le preferenze per singola categoria.",
          "In qualsiasi momento puoi rivedere le tue scelte cliccando il link \"Preferenze Cookie\" nel footer di ogni pagina o il pulsante qui sotto.",
        ],
      },
      {
        h: "9. Cookie di terze parti",
        p: [
          "Alcune tecnologie possono essere fornite da terze parti (es. bandiere hostate su flagcdn.com, embed di contenuti social). L'attivazione dei relativi cookie avviene solo dopo il tuo consenso e nel rispetto delle informative delle singole piattaforme.",
        ],
      },
      {
        h: "10. Contatti",
        p: [
          "Per domande sulla nostra Cookie Policy scrivi a: info@tesorideuropa.com.",
        ],
      },
    ],
  },
  en: {
    eyebrow: "— Legal / Cookie Policy",
    titleA: "Cookie",
    titleB: "Policy.",
    description:
      "This page describes which cookies and tracking technologies Tesori d'Europa uses, for what purposes and how you can manage your preferences.",
    managePreferences: "Manage my cookie preferences",
    lastUpdated: "Last updated: February 2026.",
    sections: [
      {
        h: "1. What cookies are",
        p: [
          "Cookies are small text files saved on your device when you visit a website. They make the site work, remember your preferences and — if authorized — collect statistical or marketing information.",
        ],
      },
      {
        h: "2. Cookie categories we use",
        p: [
          "We use cookies grouped in the following categories. Only \"Necessary\" ones are active by default; all others require your explicit consent.",
        ],
      },
      {
        h: "3. Necessary",
        p: [
          "These cookies are strictly necessary for the site to function. They enable navigation, use of the InBetween form and language switching. They do not require consent as without them the service cannot be delivered.",
        ],
      },
      {
        h: "4. Functionality",
        p: [
          "Enable advanced features such as remembering your preferred language, display preferences and form interactions. Activated only with your consent.",
        ],
      },
      {
        h: "5. Experience",
        p: [
          "Improve the quality of the user experience and enable interactions with external media (e.g. embedded videos, maps). Active only with your consent.",
        ],
      },
      {
        h: "6. Measurement",
        p: [
          "Help us understand in aggregate form how visitors use the site (most-read pages, time spent, geographic origin) to improve editorial content. Active only with your consent.",
        ],
      },
      {
        h: "7. Marketing",
        p: [
          "Allow delivery of personalized content and measurement of communication campaigns. Currently, Tesori d'Europa does not use this cookie category but may activate it in the future with your explicit consent.",
        ],
      },
      {
        h: "8. How to manage preferences",
        p: [
          "On your first visit to the site you are shown the \"Your Privacy Choices\" panel. You can accept all cookies, reject them or customize preferences by individual category.",
          "You can review your choices at any time by clicking the \"Cookie Preferences\" link in the footer of any page or the button below.",
        ],
      },
      {
        h: "9. Third-party cookies",
        p: [
          "Some technologies may be provided by third parties (e.g. country flags hosted on flagcdn.com, social embeds). Related cookies are activated only after your consent and in compliance with the individual platforms' policies.",
        ],
      },
      {
        h: "10. Contact",
        p: [
          "For questions about our Cookie Policy write to: info@tesorideuropa.com.",
        ],
      },
    ],
  },
};
